import React from 'react';
import { Box, Typography } from '@mui/material';
import { Table as AntTable, Tag, Button as AntButton, Space, Input, Spin, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { Crypter } from '../../../Functions/Security/Crypter';
import { apiEndpoints } from '../../../config';
import SweetAlert from 'sweetalert2';

interface Tab2Props {
  form?: any;
  disabled?: boolean;
  isConsultMode?: boolean;
  selectedModule: any;
  groupesAffectes: any[];
  setGroupesAffectes: (g: any[]) => void;
  allGroupes: any[];
  authHeader: () => string;
  user: any;
}

const colonnesGroupes: ColumnsType<any> = [
  { title: 'Code Groupe', dataIndex: 'CodGroup', key: 'CodGroup', width: 140 },
  { title: 'Libellé', dataIndex: 'Libelle', key: 'Libelle', width: 250 },
];

function Tab2(props: Tab2Props) {
  const [loading, setLoading] = React.useState(false);
  const [selectedAvailableKeys, setSelectedAvailableKeys] = React.useState<React.Key[]>([]);
  const [selectedAffectedKeys, setSelectedAffectedKeys] = React.useState<React.Key[]>([]);
  const [searchAvailable, setSearchAvailable] = React.useState('');
  const [searchAffected, setSearchAffected] = React.useState('');

  const codModule = props.selectedModule?.CodModule;

  const groupesAffectesSafe = React.useMemo(() =>
    Array.isArray(props.groupesAffectes) ? props.groupesAffectes : []
    , [props.groupesAffectes]);

  // Groupes disponibles = tous les groupes - ceux déjà affectés
  const groupesDisponibles = React.useMemo(() => {
    const affectedCodes = new Set(groupesAffectesSafe.map((g: any) => g.CodGroup));
    let available = (Array.isArray(props.allGroupes) ? props.allGroupes : []).filter((g: any) => !affectedCodes.has(g.CodGroup));
    if (searchAvailable) {
      const lower = searchAvailable.toLowerCase();
      available = available.filter(
        (g: any) =>
          (g.CodGroup || '').toLowerCase().includes(lower) ||
          (g.Libelle || '').toLowerCase().includes(lower)
      );
    }
    return available;
  }, [props.allGroupes, groupesAffectesSafe, searchAvailable]);

  const groupesAffectesFiltres = React.useMemo(() => {
    if (!searchAffected) return groupesAffectesSafe;
    const lower = searchAffected.toLowerCase();
    return groupesAffectesSafe.filter(
      (g: any) =>
        (g.CodGroup || '').toLowerCase().includes(lower) ||
        (g.Libelle || '').toLowerCase().includes(lower)
    );
  }, [groupesAffectesSafe, searchAffected]);

  const callAffecter = (groupCodes: string[], isBulk: boolean) => {
    if (!codModule) {
      SweetAlert.fire({ title: 'Attention', text: 'Aucun module sélectionné', icon: 'warning' });
      return;
    }

    const endpoint = isBulk ? 'AffecterGroupeBulk' : 'AffecterGroupe';
    const moduleData = {
      CodModule: codModule,
      CodGroup: groupCodes.join(','),
      UtiSaisie: props.user.coduti,
    };
    const data = Crypter.encrypt(JSON.stringify(moduleData));
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/${endpoint}`,
      headers: {
        Authorization: props.authHeader(),
        'Content-Type': 'application/json',
      },
      data: data,
    };
    axios
      .request(config)
      .then((response: any) => {
        setLoading(false);
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data)).Value;
          if (res.StatutCode == 200 || res.StatutCode == 201) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
            setSelectedAvailableKeys([]);
            reloadGroupesParModule();
          } else {
            SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
          }
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
      });
  };

  const handleAffecter = () => {
    if (selectedAvailableKeys.length === 0) {
      SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez au moins un groupe à affecter', icon: 'warning' });
      return;
    }
    const groupCodes = selectedAvailableKeys.map((key) => {
      const item = groupesDisponibles.find((g) => g.CodGroup === key);
      return item?.CodGroup;
    }).filter(Boolean) as string[];

    SweetAlert.fire({
      title: 'Affectation',
      text: `Affecter ${groupCodes.length} groupe(s) au module ${codModule} ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'Non',
      icon: 'question',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        callAffecter(groupCodes, groupCodes.length > 1);
      }
    });
  };

  const handleAffecterSingle = (codGroup: string) => {
    SweetAlert.fire({
      title: 'Affectation',
      text: `Affecter le groupe ${codGroup} au module ${codModule} ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      confirmButtonColor: '#116599',
      cancelButtonColor: 'darkred',
      cancelButtonText: 'Non',
      icon: 'question',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        callAffecter([codGroup], false);
      }
    });
  };

  const handleRetirer = () => {
    if (selectedAffectedKeys.length === 0) {
      SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez au moins un groupe à retirer', icon: 'warning' });
      return;
    }
    const groupCodes = selectedAffectedKeys.map((key) => {
      const item = props.groupesAffectes.find((g: any) => g.CodGroup === key);
      return item?.CodGroup;
    }).filter(Boolean);

    SweetAlert.fire({
      title: 'Retrait',
      text: `Retirer ${groupCodes.length} groupe(s) du module ${codModule} ?`,
      showCancelButton: true,
      confirmButtonText: 'Oui',
      confirmButtonColor: 'darkred',
      cancelButtonColor: '#6c757d',
      cancelButtonText: 'Non',
      icon: 'warning',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        const moduleData = {
          CodModule: codModule,
          CodGroup: groupCodes.join(','),
          UtiSaisie: props.user.coduti,
        };
        const data = Crypter.encrypt(JSON.stringify(moduleData));
        const config = {
          method: 'delete',
          maxBodyLength: Infinity,
          url: `${apiEndpoints.moduleBI}/RetirerGroupe`,
          headers: {
            Authorization: props.authHeader(),
            'Content-Type': 'application/json',
          },
          data: data,
        };
        axios
          .request(config)
          .then((response: any) => {
            setLoading(false);
            if (response.data) {
              const res = JSON.parse(Crypter.decrypt(response.data)).Value;
              if (res.StatutCode == 200) {
                SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                setSelectedAffectedKeys([]);
                reloadGroupesParModule();
              } else {
                SweetAlert.fire({ title: 'Attention', text: res.Message, icon: 'warning' });
              }
            }
          })
          .catch((error) => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: String(error), icon: 'error' });
          });
      }
    });
  };

  const reloadGroupesParModule = () => {
    if (!codModule) return;
    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: `${apiEndpoints.moduleBI}/ListGroupesParModule`,
      headers: {
        Accept: 'text/plain',
        Authorization: props.authHeader(),
        CypherCodModule: Crypter.encrypt(codModule),
      },
    };
    axios.request(config).then((response: any) => {
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode == 200) {
          const rawGroupes = res.Result.Table || res.Result || [];
          const groupes = (Array.isArray(rawGroupes) ? rawGroupes : []).map((g: any) => ({
            CodGroup: g.CODGROUP || g.CodGroup,
            Libelle: g.LibelleGroupe || g.Libelle || g.LIBELLE,
            Niveau: g.NIVEAU || g.Niveau,
          }));
          props.setGroupesAffectes(groupes);
        }
      }
    });
  };

  if (!codModule) {
    return (
      <div className='tabItem'>
        <Box display='flex' justifyContent='center' alignItems='center' height={200}>
          <Typography color='textSecondary'>
            Veuillez consulter un module pour gérer ses affectations de groupes.
          </Typography>
        </Box>
      </div>
    );
  }

  const rowSelectionAvailable = {
    selectedRowKeys: selectedAvailableKeys,
    onChange: (keys: React.Key[]) => setSelectedAvailableKeys(keys),
  };

  const rowSelectionAffected = {
    selectedRowKeys: selectedAffectedKeys,
    onChange: (keys: React.Key[]) => setSelectedAffectedKeys(keys),
  };

  return (
    <div className='tabItem'>
      <Spin spinning={loading}>
        <Box fontStyle='inherit' className='border' width={'88%'} position={'relative'}>
          {/* Info module en lecture seule */}
          <Box display='flex' fontStyle='inherit' className='border-left' sx={{ p: 1, mb: 1 }}>
            <Typography variant='body1' fontWeight='bold' sx={{ mr: 3 }}>
              Module: <span style={{ color: '#3b82f6' }}>{codModule}</span>
            </Typography>
            <Typography variant='body1'>
              {props.selectedModule?.Libelle || ''}
            </Typography>
          </Box>

          {!props.isConsultMode && (
            <Box display='flex' sx={{ p: 1, mb: 1, gap: 1 }}>
              <Space>
                <AntButton type='primary' onClick={handleAffecter} disabled={props.disabled || selectedAvailableKeys.length === 0}>
                  Affecter ({selectedAvailableKeys.length})
                </AntButton>
                <AntButton danger onClick={handleRetirer} disabled={props.disabled || selectedAffectedKeys.length === 0}>
                  Retirer ({selectedAffectedKeys.length})
                </AntButton>
              </Space>
            </Box>
          )}

          <Box display='flex' gap={2} sx={{ p: 1 }}>
            {/* Panel gauche: Groupes disponibles */}
            <Box flex={1} className='border' sx={{ borderRadius: 2, p: 1 }}>
              <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='h6' fontWeight='bold' color='primary'>
                  Groupes disponibles ({groupesDisponibles.length})
                </Typography>
                <Input.Search
                  placeholder='Rechercher...'
                  allowClear
                  size='small'
                  style={{ width: 200 }}
                  onChange={(e) => setSearchAvailable(e.target.value)}
                />
              </Box>
              <AntTable
                size='small'
                columns={colonnesGroupes}
                dataSource={groupesDisponibles}
                rowKey='CodGroup'
                pagination={{ pageSize: 6, showSizeChanger: false }}
                scroll={{ y: 250 }}
                rowSelection={{
                  ...rowSelectionAvailable,
                  getCheckboxProps: () => ({ disabled: props.isConsultMode }),
                }}
              />
            </Box>

            {/* Panel droit: Groupes affectés */}
            <Box flex={1} className='border' sx={{ borderRadius: 2, p: 1 }}>
              <Box display='flex' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                <Typography variant='h6' fontWeight='bold' color='success.main'>
                  Groupes affectés ({groupesAffectesFiltres.length})
                </Typography>
                <Input.Search
                  placeholder='Rechercher...'
                  allowClear
                  size='small'
                  style={{ width: 200 }}
                  onChange={(e) => setSearchAffected(e.target.value)}
                />
              </Box>
              <AntTable
                size='small'
                columns={colonnesGroupes}
                dataSource={groupesAffectesFiltres}
                rowKey='CodGroup'
                pagination={{ pageSize: 6, showSizeChanger: false }}
                scroll={{ y: 250 }}
                rowSelection={{
                  ...rowSelectionAffected,
                  getCheckboxProps: () => ({ disabled: props.isConsultMode }),
                }}
              />
            </Box>
          </Box>
        </Box>


      </Spin>
    </div>
  );
}

export default Tab2;
